import {IntlShape} from 'react-intl';
import dayjs from 'dayjs';
import {ContractStatusManualEnum, CurrencyEnum, LanguageEnum, TitleEnum} from '../../../../enums';
import {BlockStatusEnum} from '../../../common/RfidTable/RfidTable.types';
import {TimeMapType} from '../../../reports/ReportCard/ReportCard.types';
import {UserFormType} from '../user';
import {CompanyRegistrationRequest, NewDataAddressForCompany} from 'src/types';

import {CreateCompanyFormType} from '../company';


export const loginInitialValues = {
    emailAddress: '',
    password: '',
};

export const initContractGeneral = {
    contractCreatedAt: dayjs(dayjs().format('YYYY-MM-DD')).valueOf(),
    operationalAt: dayjs(dayjs().format('YYYY-MM-DD')).valueOf(),
    contractId: '',
    contractStatus: ContractStatusManualEnum.INSTALLATION,
    lastInvoicedWattsConsumed: 0,
    totalTariffPeriodWattsConsumed: 0,
    lastAmount: 0,
    userId: '',
};

export enum TimeMapEnum {
    LAST_WEEK = 1,
    LAST_MONTH = 2,
    THIS_YEAR = 3,
    ALL = 4,
    CUSTOM = 5,
    CONTRACT = 6,
}

export const timeMap = (intl: IntlShape, handleCustomClick: () => void): TimeMapType => ({
    [TimeMapEnum.LAST_WEEK]: {
        title: intl.formatMessage({id: 'lastWeek'}),
        dateFrom: dayjs.utc().subtract(1, 'week').valueOf(),
    },
    [TimeMapEnum.LAST_MONTH]: {
        title: intl.formatMessage({id: 'last30days'}),
        dateFrom: dayjs.utc().subtract(1, 'month').valueOf(),
    },
    [TimeMapEnum.THIS_YEAR]: {
        title: intl.formatMessage({id: 'thisYear'}),
        dateFrom: dayjs.utc().startOf('year').valueOf(),
    },
    [TimeMapEnum.ALL]: {
        title: intl.formatMessage({id: 'all'}),
        dateFrom: dayjs.utc('2020-01-01').valueOf(),
    },
    [TimeMapEnum.CUSTOM]: {
        title: intl.formatMessage({id: 'custom'}),
        dateFrom: 0,
        onClick: handleCustomClick,
    },
});

export const signUpInitialValues = {
    city: '',
    country: '',
    region: '',
    houseNumber: '',
    street: '',
    zipCode: '',
    businessId: 1,
    emailAddress: '',
    password: '',
    confirmPassword: '',
    parkingLot: '',
    firstName: '',
    lastName: '',
    language: LanguageEnum.ENGLISH,
    agreements: false,
};

export type SignUpCorporateType = Omit<
    CompanyRegistrationRequest,
    'parkingAddress' | 'businessId'
> &
    NewDataAddressForCompany & {
    agreements: boolean;
};

export const signUpCompanyInitialValues: SignUpCorporateType = {
    companyName: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    stationsQuantity: '',
    city: '',
    region: '',
    country: '',
    houseNumber: '',
    street: '',
    zipCode: '',
    parkingPlace: '',
    agreements: false,
};

export const feedbackInitialValues = {
    // phone: '',
    message: '',
};

export const locationInitialValues = {
    name: '',
    country: '',
    region: '',
    city: '',
    zipCode: '',
    street: '',
    houseNumber: '',
    parkingLots: 0,
    availableAmperes: 0,
    locationType: undefined,
    realEstateId: null,
    // Empty rather than 0. A zero sitting in the field by default is a price nobody chose, and a
    // location priced at nothing cannot have its sessions costed, closed or invoiced - the server
    // refuses it now, and the form should ask for a number instead of shipping a silent zero.
    defaultPrice: '',
    currency: 'CHF',
    guestPricePerKwh: '',
    guestHoldAmount: '',
};

export const createContractManuallySteps = (intl: IntlShape) => [
    {
        title: intl.formatMessage({id: 'generalInfo'}),
        description: intl.formatMessage({id: 'clientContractInfo'}),
    },
    {
        title: intl.formatMessage({id: 'tariffs'}),
        description: intl.formatMessage({id: 'chooseTariff'}),
    },
    {
        title: intl.formatMessage({id: 'contract'}),
        description: intl.formatMessage({id: 'uploadDocument'}),
    },
];

export const rfidStatuses = (intl: IntlShape) => [
    {
        id: 0,
        name: intl.formatMessage({id: BlockStatusEnum.blocked}),
        value: BlockStatusEnum.blocked,
    },
    {
        id: 1,
        name: intl.formatMessage({id: BlockStatusEnum.accepted}),
        value: BlockStatusEnum.accepted,
    },
];

export const currencies = (intl: IntlShape) => [
    {
        id: 0,
        name: intl.formatMessage({id: 'EUR'}),
        value: CurrencyEnum.EUR,
    },
    {
        id: 1,
        name: intl.formatMessage({id: 'CHF'}),
        value: CurrencyEnum.CHF,
    },
    {
        id: 2,
        name: intl.formatMessage({id: 'UAH'}),
        value: CurrencyEnum.UAH,
    },
];

export const titles = (intl: IntlShape) => [
    {
        id: 0,
        name: intl.formatMessage({id: 'miss'}),
        value: TitleEnum.MISS,
    },
    {
        id: 1,
        name: intl.formatMessage({id: 'mr'}),
        value: TitleEnum.MR,
    },
    {
        id: 2,
        name: intl.formatMessage({id: 'mrs'}),
        value: TitleEnum.MRS,
    },
    {
        id: 4,
        name: intl.formatMessage({id: 'other'}),
        value: TitleEnum.OTHER,
    },
];

export const filterInitialValues = {
    countries: '',
    city: '',
};

export const AddUserInitialValue: UserFormType = {
    cityParking: '',
    countryParking: '',
    regionParking: '',
    houseNumberParking: '',
    streetParking: '',
    zipCodeParking: '',
    parkingLotParking: '',
    cityHome: '',
    countryHome: '',
    regionHome: '',
    houseNumberHome: '',
    streetHome: '',
    zipCodeHome: '',
    businessId: 0,
    emailAddress: '',
    firstName: '',
    lastName: '',
    language: LanguageEnum.FRENCH,
    phoneNumber: '',
    title: '',
    useParkingAddress: false,
};

export enum UserTypeEnum {
    NOT_SELECTED = 'NOT_SELECTED',
    PRIVATE_DRIVER = 'PRIVATE_DRIVER',
    COMPANY_USER = 'COMPANY_USER',
}

export const userTypes = (intl: IntlShape) => [
    {
        id: 0,
        name: ' ',
        value: UserTypeEnum.NOT_SELECTED,
    },
    {
        id: 1,
        name: intl.formatMessage({id: 'privateDriver'}),
        value: UserTypeEnum.PRIVATE_DRIVER,
    },
    {
        id: 2,
        name: intl.formatMessage({id: 'companyUser'}),
        value: UserTypeEnum.COMPANY_USER,
    },
];

export const chargerInitialValues = {
    chargePointId: '',
    chargePointName: '',
    locationId: '',
    modelId: '',
    userId: '',
    companyId: '',
    userType: UserTypeEnum.NOT_SELECTED,
};

export const tariffInitialValues = {
    name: '',
    country: '',
    region: '',
    nameFrench: '',
    nameDeutsch: '',
    description: '',
    descriptionFrench: '',
    descriptionDeutsch: '',
    currency: 'EUR',
    monthlyFee: 0,
    contractPeriodLimit: 0,
    businessId: 0,
};

export const forgotPasswordFormInitialValues = {
    emailAddress: '',
};

export const resetPasswordFormInitialValues = {
    password: '',
    confirmPassword: '',
};

export const addClientToLocationFormValues = {
    userId: '',
    parkingPlace: '',
};

export const contractInitialValues = {
    language: 'ENGLISH',
    phoneNumber: '',
    city: '',
    country: '',
    region: '',
    houseNumber: '',
    street: '',
    zipCode: '',
    useParkingAddress: true,
};

export const tariffDescriptionValues = {
    description: '',
    descriptionFrench: '',
};

export const AddBusinessInitialValue = {
    city: '',
    country: '',
    region: '',
    houseNumber: '',
    street: '',
    zipCode: '',
    emailAddress: '',
    name: '',
    language: '',
    shareClients: false,
    userPerMonthCost: 0,
    userPerMonthCurrency: CurrencyEnum.EUR,
    // What the business will bill in — the default behind every price, cost and figure it sees later.
    defaultCurrency: CurrencyEnum.CHF,
};

export const addCompanyInitialValue: CreateCompanyFormType = {
    businessId: 1,
    companyName: '',
    billingEmail: undefined,
    // French, like the business itself: AMP IT is in Geneva and most of the people
    // being created here read French. German was a leftover default nobody chose.
    language: LanguageEnum.FRENCH,
    loginEmail: '',
    billingAddressCity: '',
    billingAddressCountry: '',
    billingAddressHouseNumber: '',
    billingAddressParkingPlace: '',
    billingAddressRegion: '',
    billingAddressStreet: '',
    billingAddressZipCode: '',
    contactPersonFirstName: '',
    contactPersonLastName: '',
    contactPersonPhone: '',
    parkingAddressCity: '',
    parkingAddressCountry: '',
    parkingAddressHouseNumber: '',
    parkingAddressParkingPlace: '',
    parkingAddressRegion: '',
    parkingAddressStreet: '',
    parkingAddressZipCode: '',
    isParkingAddressSameAsBillingAddress: false,
    // On by default: consumption has always been billed, and a company created with it silently off
    // would be undercharged for a month before anyone noticed.
    chargeConsumption: true,
};

export const scheduleInitialValue = {
    userId: '',
    startDay: '',
    startHour: '',
    startMinute: '',
    stopDay: '',
    stopHour: '',
    stopMinute: '',
    type: false,
};

export const managersInitialValue = {
    firstName: '',
    lastName: '',
    language: '',
    userType: '',
    email: '',
    title: '',
    phoneNumber: '',
};

export const reOwnersInitialValue = {
    businessId: 0,
    name: '',
};
